import React from 'react'
import {AppExclusiveDealLabel} from 'components/AppExclusiveDealLabel'
import {OfferLogo} from 'components/Offer/Shared/OfferLogo'
import {shouldOfferBeBranded} from 'components/Offer/Shared/utils'
import {SplitBookingOfferInfo} from 'components/Offer/types'
import {SearchOffer} from 'modules/sapiSearch/slice'

import {Box} from '@vio/atlas/helpers/Box'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {OfferSecondaryLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'

interface MainProps {
  hotelId: string
  isSplitBooking?: false
  variant: OfferSecondaryLayoutVariant
  offer: SearchOffer
  isAppLockedDeal: boolean
  splitBookingOffers?: never
}

interface SplitBookingProps {
  hotelId: string
  isSplitBooking: true
  splitBookingOffers: SplitBookingOfferInfo[]
  variant: OfferSecondaryLayoutVariant
  isAppLockedDeal?: never
  offer?: never
}

export const ProviderLogoComponent: React.FC<MainProps | SplitBookingProps> = ({
  hotelId,
  offer,
  isSplitBooking,
  isAppLockedDeal,
  splitBookingOffers = [],
  variant
}) => {
  const {brandCode} = useBrand()
  const {
    monetisation: {hasBrandedOffers},
    commerce: {bookOnBrand}
  } = useBrandCapabilities()
  const isBookOnBrand = bookOnBrand.enabled

  if (isSplitBooking) {
    const firstOffer = splitBookingOffers[0]?.offer
    const secondOffer = splitBookingOffers[1]?.offer
    const isSameProvider =
      firstOffer?.intermediaryProvider === secondOffer?.intermediaryProvider

    const isFirstOfferBranded = shouldOfferBeBranded({
      offerIntermediaryProviderCode: firstOffer?.intermediaryProvider,
      isBookOnBrand,
      hasBrandedOffers
    })

    const isSecondOfferBranded = shouldOfferBeBranded({
      offerIntermediaryProviderCode: secondOffer?.intermediaryProvider,
      isBookOnBrand,
      hasBrandedOffers
    })

    if (variant === 'SecondaryInline')
      return (
        <OfferLogo
          hotelId={hotelId}
          offer={firstOffer}
          isSecondaryInlineOffer
          providerCodeOverride={isFirstOfferBranded && brandCode}
        />
      )

    return (
      <Box
        height={variant === 'SecondaryDefault' ? '20px' : 'auto'}
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap="s200"
      >
        <OfferLogo
          hotelId={hotelId}
          offer={firstOffer}
          variant="iconAndName"
          size={variant === 'SecondaryWithoutRoomName' ? 'sm' : 'md'}
          isSplitBooking
          providerCodeOverride={isFirstOfferBranded && brandCode}
        />
        {!isSameProvider && (
          <>
            +
            <OfferLogo
              hotelId={hotelId}
              offer={secondOffer}
              variant="iconAndName"
              size={variant === 'SecondaryWithoutRoomName' ? 'sm' : 'md'}
              isSplitBooking
              providerCodeOverride={isSecondOfferBranded && brandCode}
            />
          </>
        )}
      </Box>
    )
  }

  const {intermediaryProvider} = offer || {}

  const isBrandedDeal = shouldOfferBeBranded({
    offerIntermediaryProviderCode: intermediaryProvider,
    isBookOnBrand,
    hasBrandedOffers
  })

  if (variant === 'SecondaryInline')
    return (
      <OfferLogo
        hotelId={hotelId}
        offer={offer}
        isSecondaryInlineOffer
        providerCodeOverride={(isAppLockedDeal || isBrandedDeal) && brandCode}
        isAppLockedDeal={isAppLockedDeal}
      />
    )

  return (
    <Box display="flex" gap="s200">
      <Box
        height={variant === 'SecondaryDefault' ? '20px' : 'auto'}
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap="s200"
      >
        <OfferLogo
          hotelId={hotelId}
          offer={offer}
          size={variant === 'SecondaryWithoutRoomName' ? 'sm' : 'md'}
          variant={isAppLockedDeal ? 'icon' : 'iconAndName'}
          providerCodeOverride={(isAppLockedDeal || isBrandedDeal) && brandCode}
          isAppLockedDeal={isAppLockedDeal}
        />
      </Box>
      {isAppLockedDeal && <AppExclusiveDealLabel variant="titleS" />}
    </Box>
  )
}
